/**
 * PII Masking Utilities for Demo Mode
 *
 * These functions mask personally identifiable information (PII)
 * when demo mode is active, making it safe to show screens during
 * presentations or screen sharing.
 */

/**
 * Masks an email address, showing first and last character of local part
 * Example: john.doe@example.com -> j***e@***.com
 */
export const maskEmail = (email: string | null | undefined): string => {
  if (!email) return '';

  const [localPart, domain] = email.split('@');
  if (!domain) return '***@***.com';

  const maskedLocal =
    localPart.length <= 2
      ? '***'
      : `${localPart[0]}***${localPart[localPart.length - 1]}`;

  const domainParts = domain.split('.');
  const maskedDomain =
    domainParts.length > 1
      ? `***.${domainParts[domainParts.length - 1]}`
      : '***';

  return `${maskedLocal}@${maskedDomain}`;
};

/**
 * Masks a name, showing first and last character
 * Example: John -> J***n
 */
export const maskName = (name: string | null | undefined): string => {
  if (!name || name.length === 0) return '';
  if (name.length === 1) return '*';
  if (name.length === 2) return `${name[0]}*`;

  return `${name[0]}***${name[name.length - 1]}`;
};

/**
 * Masks a full name (given name + surname)
 */
export const maskFullName = (
  givenName: string | null | undefined,
  surname: string | null | undefined,
): string => {
  const maskedGiven = maskName(givenName);
  const maskedSurname = maskName(surname);

  if (!maskedGiven && !maskedSurname) return '';
  if (!maskedGiven) return maskedSurname;
  if (!maskedSurname) return maskedGiven;

  return `${maskedGiven} ${maskedSurname}`;
};

/**
 * Masks a phone number, showing only last 4 digits
 * Example: +44 7911 123456 -> ***3456
 */
export const maskPhone = (phone: string | null | undefined): string => {
  if (!phone) return '';

  // Remove non-digit characters for processing
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '****';

  return `***${digits.slice(-4)}`;
};

/**
 * Masks a date of birth, showing only the year
 * Example: 1990-05-15 -> 1990
 */
export const maskDob = (dob: string | Date | null | undefined): string => {
  if (!dob) return '';

  let date: Date;
  if (typeof dob === 'string') {
    date = new Date(dob);
  } else {
    date = dob;
  }

  if (isNaN(date.getTime())) return '';

  return date.getFullYear().toString();
};

/**
 * Masks coordinates by rounding to 0.1 degree precision (~10km accuracy)
 * This provides approximate location without revealing exact position
 */
export const maskCoordinates = (
  lat: number | null | undefined,
  lng: number | null | undefined,
): { lat: number; lng: number } | null => {
  if (lat == null || lng == null) {
    return null;
  }

  return {
    lat: Math.round(lat * 10) / 10,
    lng: Math.round(lng * 10) / 10,
  };
};

/**
 * Masks a single coordinate value
 */
export const maskCoordinate = (
  coord: number | null | undefined,
): number | null => {
  if (coord === null || coord === undefined) return null;
  return Math.round(coord * 10) / 10;
};

/**
 * Masks an address by showing only the city/region
 * Example: 123 Main St, London, UK -> ***, London, UK
 */
export const maskAddress = (address: string | null | undefined): string => {
  if (!address) return '';

  // Simple approach: mask everything before the last two comma-separated parts
  const parts = address.split(',').map((p) => p.trim());

  if (parts.length <= 2) {
    return parts.map((p) => (p.length > 3 ? `${p[0]}***` : '***')).join(', ');
  }

  // Keep last two parts (usually city and country), mask the rest
  const maskedParts = parts.map((part, index) => {
    if (index >= parts.length - 2) return part;
    return '***';
  });

  return maskedParts.join(', ');
};

/**
 * Masks a vehicle registration/license plate
 * Example: AB12 CDE -> A***E
 */
export const maskVehicleReg = (reg: string | null | undefined): string => {
  if (!reg) return '';
  const cleaned = reg.replace(/\s/g, '');
  if (cleaned.length <= 2) return '***';
  return `${cleaned[0]}***${cleaned[cleaned.length - 1]}`;
};

/**
 * Generic ID masker - shows first 4 and last 4 characters
 * Example: abc123def456 -> abc1***f456
 */
export const maskId = (id: string | null | undefined): string => {
  if (!id) return '';
  if (id.length <= 8) return '***';
  return `${id.slice(0, 4)}***${id.slice(-4)}`;
};
